// src/pages/auth/VerifyEmailPage.jsx
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { verifyEmail } from '@/services/apiService';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle } from 'lucide-react';
import AuthLayout from '@/layouts/AuthLayout';

export const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyTokenFromUrl = async () => {
      if (!token) {
        setStatus('error');
        setMessage('No verification token provided.');
        return;
      }
      try {
        await verifyEmail(token);
        setStatus('success');

        // Clear all tokens — user needs to log in fresh
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshToken');
        sessionStorage.removeItem('user');

        setTimeout(() => navigate('/login'), 3000);
      } catch (err) {
        setStatus('error');
        setMessage(
          err?.response?.data?.error ||
          'Failed to verify email. The link may have expired. Please request a new one.'
        );
      }
    };

    verifyTokenFromUrl();
  }, [token, navigate]);

  return (
    <AuthLayout heading="Email Verification">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="flex flex-col items-center gap-4 py-4 text-center"
      >
        {/* Loading */}
        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-mikado-yellow" />
            <p className="text-swan/60 text-sm">Verifying your email address...</p>
          </>
        )}

        {/* Success */}
        {status === 'success' && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 bg-green-500/10 rounded-full">
              <CheckCircle className="text-green-500" size={28} />
            </div>
            <div>
              <h3 className="text-white font-semibold mb-1">Email Verified!</h3>
              <p className="text-swan/60 text-sm">Redirecting you to login in 3 seconds...</p>
            </div>
            <Button onClick={() => navigate('/login')} className="w-full mt-2">
              Go to Login Now
            </Button>
          </>
        )}

        {/* Error */}
        {status === 'error' && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 bg-red-500/10 rounded-full">
              <XCircle className="text-red-500" size={28} />
            </div>
            <div>
              <h3 className="text-white font-semibold mb-1">Verification Failed</h3>
              <p className="text-swan/60 text-sm">{message}</p>
            </div>
            <div className="flex flex-col gap-2 w-full mt-2">
              <Button onClick={() => navigate('/login')} className="w-full">
                Back to Login
              </Button>
              <Button
                onClick={() => navigate('/signup')}
                variant="outline"
                className="w-full"
              >
                Sign Up Again
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </AuthLayout>
  );
};